import { useState, useEffect, useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import { GAME_STAGES, DEFAULT_ROOM } from "../../constants/gameStages";
import { SocketContext } from "../../context/SocketProvider";
import { useLoading } from "../../context/LoadingContext";
import { useTimer } from "../../context/TimerContext";
import { useGameStage } from "../../hooks/useGameStage";
import { useGameDataSync } from "../../hooks/useGameDataSync";
import { useRoleBackground } from "../../hooks/useRoleBackground";
import { useSettings } from "../../context/SettingsContext";
import { useLanguage } from "../../context/LanguageContext";
import { FaChevronRight } from "react-icons/fa6";

function GuesserGuess() {
  const { socket } = useContext(SocketContext);
  const { updateStage } = useGameStage();
  const { gameData, allImages } = useGameDataSync();
  const [selectedGuess, setSelectedGuess] = useState(null);
  const { loading, setLoading } = useLoading();
  const { isExpired } = useTimer();
  const navigate = useNavigate();
  const { bgStyle, className } = useRoleBackground("blue");
  const { imageCount } = useSettings();
  const { t } = useLanguage();

  // Reset and start timer to 45 seconds when guesser navigates to this page
  useEffect(() => {
    socket.emit("reset_timer", { room: DEFAULT_ROOM, duration: 60 });
    socket.emit("start_timer", { room: DEFAULT_ROOM, duration: 60 });
  }, [socket]);

  useEffect(() => {
    if (gameData && allImages.length > 0) {
      setLoading(false);
    }
  }, [gameData, allImages]);

  if (!gameData || !allImages.length) {
    return (
      <div className="min-h-screen bg-aicom_blue flex flex-col justify-center items-center">
        <div className="text-white text-2xl">{t('describer.loading', 'Loading Images')}</div>
      </div>
    );
  }

  const handleImageClick = (index) => {
    setSelectedGuess(index);
  };

  const handleSubmitGuess = () => {
    // Save the guess result
    // TODO implemet the submit after timeout mechanism
    const guessResult = {
      ...gameData,
      guessedImageIndex: selectedGuess,
      guessedImage: selectedGuess !== null ? allImages[selectedGuess] : null,
      originalImageIndex: gameData.selectedImage.originalIndex || 0,
      isCorrect: selectedGuess === (gameData.selectedImage.originalIndex || 0),
    };

    // Emit the guess result to the server
    socket.emit("guesser_submit", {
      room: DEFAULT_ROOM,
      // Use !== null to handle index 0 correctly (0 is falsy but valid)
      image_guesser: selectedGuess !== null ? allImages[selectedGuess].path : null,
      guessResult: guessResult,
    });
    // Reset and pause timer for all clients
    socket.emit("reset_timer", { room: DEFAULT_ROOM });
    updateStage(GAME_STAGES.RESULTS);
    navigate("/aiwaiting");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-aicom_blue flex flex-col justify-center items-center">
        <div className="text-white text-2xl">{t('guesser.loadingGame', 'Loading game...')}</div>
      </div>
    );
  }

  if (!gameData || !allImages.length) {
    return (
      <div className="min-h-screen bg-aicom_blue flex flex-col justify-center items-center">
        <div className="text-white text-2xl mb-4">{t('guesser.gameDataNotFound', 'Game data not found')}</div>
        <Link to="/pickingimage">
          <button className="bg-aicom_blue text-white px-6 py-3 rounded font-bold">
            {t('guesser.startNewGame', 'Start New Game')}
          </button>
        </Link>
      </div>
    );
  }

  return (
    <div
      className={`p-[64px] flex flex-col items-center ${className}`}
      style={bgStyle}
    >
      <div className="w-[1052px] bg-white rounded-lg shadow-lg text-center">
        <h2
          className={`bg-blue400 w-full rounded-t-lg font-bold text-white text-xl font-[22px] py-[11px] px-[12px]`}
        >
          {t('guesser.selectImage', 'SELECT THE IMAGE YOU THINK BEST FITS THE DESCRIPTION:')}
        </h2>
        <p className="py-[24px] text-[22px] mx-[25px]">
          " <span className="font-bold">{gameData.description}</span> "
        </p>

        <div
          className={`grid pb-[24px] px-[143px] gap-[12px] items-center ${
            imageCount === 6 ? "grid-cols-3 max-w-5xl" : "grid-cols-4"
          }`}
          // TODO - fix image grid here (centering and spacing)
        >
          {allImages.map((image, index) => (
            <img
              key={index}
              src={image.url}
              alt={`Image ${index + 1}`}
              className={`size-[183px] rounded-[16px] cursor-pointer transition-all duration-200 ${
                selectedGuess === index
                  ? "border-[6px] border-aicom_blue"
                  : "hover:shadow-lg"
              } ${
                isExpired
                  ? "opacity-50 pointer-events-none cursor-not-allowed"
                  : ""
              }`}
              onClick={() => {
                if (!isExpired) {
                  handleImageClick(index);
                }
              }}
              disabled={isExpired}
            />
          ))}
        </div>

        <div className="flex gap-[24px] mb-[24px] ml-[884px]">
          <button
            className={`btn-blue-sm rounded-[10px] relative`}
            onClick={handleSubmitGuess}
            disabled={selectedGuess === null && !isExpired}
          >
            <span className="btn-text-pos-right-sm">
              {(() => {
                return isExpired ? t('timer.timesUp', "Time's up") : t('button.next', "Next");
              })()}
            </span>
            <FaChevronRight className="arrow-pos-right-sm" />
          </button>
        </div>
      </div>
    </div>
  );
}

export default GuesserGuess;
